import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  CONTROLLED_RESOURCES,
  type ControlledResource,
  type Intent,
  type ProfilePermission,
} from '../../types/profiles';

interface ProfilePermissionMatrixProps {
  permissions: ProfilePermission[];
  onChange?: (next: ProfilePermission[]) => void;
  readOnly?: boolean;
}

const INTENTS: Intent[] = ['read', 'write', 'delete'];

function defaultPermissionForResource(resource: ControlledResource): ProfilePermission {
  return { resource, read: false, write: false, delete: false };
}

function ensureAllResources(input: ProfilePermission[]): ProfilePermission[] {
  const map = new Map<ControlledResource, ProfilePermission>();
  input.forEach((p) => map.set(p.resource, { ...p }));
  return CONTROLLED_RESOURCES.map((r) => map.get(r) ?? defaultPermissionForResource(r));
}

export const ProfilePermissionMatrix: React.FC<ProfilePermissionMatrixProps> = ({
  permissions,
  onChange,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const normalized = ensureAllResources(permissions);

  const updateCell = (resource: ControlledResource, intent: Intent, value: boolean) => {
    if (!onChange || readOnly) return;
    const next = normalized.map((p) => {
      if (p.resource !== resource) return p;
      const updated = { ...p, [intent]: value };
      // "write" and "delete" imply "read" for UX consistency.
      if ((intent === 'write' || intent === 'delete') && value) {
        updated.read = true;
      }
      // Clearing "read" clears "write"/"delete" too.
      if (intent === 'read' && !value) {
        updated.write = false;
        updated.delete = false;
      }
      return updated;
    });
    onChange(next);
  };

  const toggleRow = (resource: ControlledResource, allOn: boolean) => {
    if (!onChange || readOnly) return;
    onChange(
      normalized.map((p) =>
        p.resource === resource ? { ...p, read: allOn, write: allOn, delete: allOn } : p
      )
    );
  };

  const bulk = (allOn: boolean) => {
    if (!onChange || readOnly) return;
    onChange(
      CONTROLLED_RESOURCES.map((r) => ({
        resource: r,
        read: allOn,
        write: allOn,
        delete: allOn,
      }))
    );
  };

  return (
    <div className="space-y-2">
      {!readOnly && (
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => bulk(true)}
            className="px-2 py-1 rounded border border-border hover:bg-muted/40"
          >
            {t('profiles.matrix.selectAll', 'Select all')}
          </button>
          <button
            type="button"
            onClick={() => bulk(false)}
            className="px-2 py-1 rounded border border-border hover:bg-muted/40"
          >
            {t('profiles.matrix.clearAll', 'Clear all')}
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 font-medium">
                {t('profiles.matrix.resource', 'Resource')}
              </th>
              {INTENTS.map((intent) => (
                <th key={intent} className="px-3 py-2 font-medium text-center capitalize">
                  {t(`profiles.matrix.${intent}`, intent)}
                </th>
              ))}
              {!readOnly && (
                <th className="px-3 py-2 font-medium text-right">
                  {t('profiles.matrix.row', 'Row')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {normalized.map((perm) => (
              <tr key={perm.resource} className="border-t border-border/60">
                <td className="px-3 py-2 font-mono text-xs">{perm.resource}</td>
                {INTENTS.map((intent) => (
                  <td key={intent} className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={perm[intent]}
                      disabled={readOnly}
                      onChange={(e) => updateCell(perm.resource, intent, e.target.checked)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                ))}
                {!readOnly && (
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => toggleRow(perm.resource, !(perm.read && perm.write && perm.delete))}
                      className="text-xs text-primary hover:underline"
                    >
                      {perm.read && perm.write && perm.delete
                        ? t('profiles.matrix.clearRow', 'Clear')
                        : t('profiles.matrix.allRow', 'All')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
